/** `/reset-password?token=...` — установка нового пароля (PULSE-269). */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { useResetPassword } from '@/hooks/useAuthApi'

interface FormValues { password: string; password2: string }

export default function ResetPassword() {
  const { t } = useTranslation(['auth', 'common'])
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const reset = useResetPassword()
  const [done, setDone] = useState(false)

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>()

  async function onSubmit(values: FormValues) {
    try {
      await reset.mutateAsync({ token, password: values.password })
      setDone(true)
    } catch { /* error surfaced below */ }
  }

  const apiError =
    reset.isError && isAxiosError(reset.error)
      ? String(reset.error.response?.data?.detail ?? t('auth:errors.login_failed'))
      : null

  return (
    <AuthLayout
      title={t('auth:reset.title')}
      subtitle={t('auth:reset.subtitle')}
      footer={
        <Link className="text-fire hover:underline" to="/login">
          {t('auth:reset.go_login')}
        </Link>
      }
    >
      {!token && (
        <div className="bg-rose-faint text-rose text-sm rounded px-3 py-2">
          {t('auth:reset.no_token')}
        </div>
      )}
      {token && done && (
        <div className="bg-pulse/10 border border-pulse/30 text-sm text-text rounded px-3 py-3">
          {t('auth:reset.done')}
        </div>
      )}
      {token && !done && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            label={t('auth:reset.password_label')}
            htmlFor="password" error={errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoFocus
              autoComplete="new-password"
              placeholder="••••••••"
              invalid={!!errors.password}
              {...register('password', {
                required: t('auth:validation.password_required'),
                minLength: { value: 8, message: t('auth:validation.password_min') },
              })}
            />
          </FormField>
          <FormField
            label={t('auth:reset.password2_label')}
            htmlFor="password2" error={errors.password2?.message}
          >
            <Input
              id="password2"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              invalid={!!errors.password2}
              {...register('password2', {
                validate: (v) => v === watch('password') || t('auth:reset.mismatch'),
              })}
            />
          </FormField>

          {apiError && (
            <div className="bg-rose-faint text-rose text-sm rounded px-3 py-2">
              {apiError}
            </div>
          )}

          <Button type="submit" className="w-full" loading={reset.isPending}>
            {t('auth:reset.submit')}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
